import type { IShortcutListItem, IShortcutListSection } from '../../contexts/AppShortcutsContext'
import { hasCommandModifier, isSingleKeyShortcutBlocked } from '../../helpers/keyboardShortcuts'
import { commandKeycap } from '../../helpers/platform'
import {
  isKeyEventInsideOverlay,
  MessagesShortcut,
  shortcutKeys,
} from '../../pages/Messages/keyboardShortcuts'

/** AIM's two chords are answered by MessagesBubble, which owns the bubble they
 *  open; they are here to be listed. */
export enum AppShortcut {
  NewAimConversation = 'newAimConversation',
  AskArc = 'askArc',
  NewTask = 'newTask',
  ShortcutList = 'shortcutList',
}

type IKeyEvent = Pick<
  KeyboardEvent,
  'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'target' | 'defaultPrevented'
>

/** ⌘/ and Ctrl+/ work while typing; Shift is not checked, since some layouts need it for `/`. */
const isShortcutListChord = (event: IKeyEvent): boolean =>
  hasCommandModifier(event) && !event.altKey && event.key === '/'

/**
 * Which app-wide shortcut a keystroke is, if any. A keystroke a page already
 * handled is left alone, which is how My Tasks and the Project Board keep N for
 * their inline row.
 */
export const matchAppShortcut = (
  event: IKeyEvent,
): AppShortcut.NewTask | AppShortcut.ShortcutList | null => {
  if (event.defaultPrevented) return null
  if (isShortcutListChord(event)) {
    return isKeyEventInsideOverlay(event) ? null : AppShortcut.ShortcutList
  }
  if (isSingleKeyShortcutBlocked(event)) return null
  if (event.key === '?') return AppShortcut.ShortcutList
  if (event.key.toLowerCase() === 'n') return AppShortcut.NewTask
  return null
}

interface IAppShortcutAvailability {
  hasAimBubble: boolean
  canCreateTask: boolean
}

export const buildAppShortcutSection = ({
  hasAimBubble,
  canCreateTask,
}: IAppShortcutAvailability): IShortcutListSection => {
  const shortcuts: IShortcutListItem[] = []
  if (hasAimBubble) {
    shortcuts.push(
      {
        id: AppShortcut.NewAimConversation,
        label: 'New AIM conversation',
        chords: [shortcutKeys(MessagesShortcut.NewConversation)],
      },
      {
        id: AppShortcut.AskArc,
        label: 'Ask Arc',
        chords: [shortcutKeys(MessagesShortcut.AskArc)],
      },
    )
  }
  if (canCreateTask) {
    shortcuts.push({ id: AppShortcut.NewTask, label: 'New task', chords: [['N']] })
  }
  shortcuts.push({
    id: AppShortcut.ShortcutList,
    label: 'Show keyboard shortcuts',
    chords: [['?'], [commandKeycap(), '/']],
  })
  return { title: 'Anywhere', shortcuts }
}

const chordKey = (chord: string[]): string => chord.join('+')

/**
 * The sheet's sections: the app-wide ones first, then each mounted surface's,
 * without the rows the app-wide section already shows (AIM's ⌘J, ACT's N).
 */
export const buildShortcutSheet = (
  appSection: IShortcutListSection,
  pageSections: IShortcutListSection[],
): IShortcutListSection[] => {
  const appChords = new Set(appSection.shortcuts.flatMap(({ chords }) => chords.map(chordKey)))
  const pageOnly = pageSections
    .map((section) => ({
      ...section,
      shortcuts: section.shortcuts.filter(
        ({ chords }) => !chords.every((chord) => appChords.has(chordKey(chord))),
      ),
    }))
    .filter((section) => section.shortcuts.length > 0)
  return [appSection, ...pageOnly]
}
